import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import {
    FAMILY_CHIP_CLASS,
    FlatDetectionTable,
    OutputBody,
    PII_TYPE_FAMILY,
    PII_TYPE_LABEL,
    type PiiType,
    type SanitizationStrategy,
    STRATEGY_OPTIONS,
    splitOutputIntoChunks,
} from '@client/components/admin/sanitize-text-pii/shared';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Collapse, Empty, Input } from 'antd';
import { Code2, Copy, Eraser, FileText, Layers, ShieldCheck, Sparkles, Wand2 } from 'lucide-react';
import { useMemo, useState } from 'react';

const { TextArea } = Input;

const MAX_INPUT_CHARS = 50_000;

const SAMPLE_TEXT = `Claimant John Smith (DOB 23.04.1984) reported a vehicle collision on 12/03/2024 at 14:30.
Contact details: john.smith@example.com, mobile 0412 345 678.
Residential address: 12 George Street, Sydney NSW 2000.
Business: Acme Pty Ltd, ABN 51 824 753 556.
Bank details for compensation: BSB 062-001 account 12345678.
Payment card on file ending 4242 4242 4242 4242 (do not store).
A second witness, Jane Doe, also at 12 George Street, confirmed the timeline.`;

type SanitizeResult = RouterOutputs['admin']['sanitization']['sanitizeText'];

export const Route = createFileRoute('/admin-console/tools/sanitise-text-pii/tool')({
    staticData: {
        breadcrumb: {
            title: 'Tool',
        },
        ui: {
            useParentTitle: true,
        },
    },
    component: SanitizeTextPiiToolPage,
});

function SanitizeTextPiiToolPage() {
    const { trpc } = useTrpc();
    const [input, setInput] = useState('');
    const [strategy, setStrategy] = useState<SanitizationStrategy>('inhouse');
    const [result, setResult] = useState<SanitizeResult | null>(null);
    const [showRaw, setShowRaw] = useState(false);

    const handleStrategyChange = (next: SanitizationStrategy) => {
        setStrategy(next);
        setResult(null);
    };

    const sanitizeMutation = trpc.admin.sanitization.sanitizeText.useMutation({
        onSuccess(data) {
            setResult(data);
            setShowRaw(false);
            const noun = data.entries.length === 1 ? 'PII item' : 'PII items';
            message.success(`Sanitised — ${data.entries.length} ${noun} replaced`);
        },
        onError(error) {
            console.error('[Admin sanitize PII] Failed', error);
            message.error(error.message || 'Sanitisation failed. Please try again.');
        },
    });

    const charCount = input.length;
    const isOverLimit = charCount > MAX_INPUT_CHARS;
    const isEmpty = charCount === 0;

    const handleSanitize = () => {
        if (isEmpty || isOverLimit) {
            return;
        }

        sanitizeMutation.mutate({
            text: input,
            strategy,
        });
    };

    const handleClear = () => {
        setInput('');
        setResult(null);
    };

    const handleLoadSample = () => {
        setInput(SAMPLE_TEXT);
        setResult(null);
    };

    const handleCopy = async () => {
        if (!result) {
            return;
        }

        try {
            await navigator.clipboard.writeText(result.text);
            message.success('Copied to clipboard');
        } catch {
            message.error('Failed to copy');
        }
    };

    const counterClass = isOverLimit
        ? 'text-error'
        : charCount > MAX_INPUT_CHARS * 0.9
          ? 'text-warning'
          : 'text-muted-foreground';

    const outputChunks = useMemo(() => (result ? splitOutputIntoChunks(result.text) : []), [result]);

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-6xl">
                <PageHeader />

                <div className="mt-2 rounded-md border border-separator bg-card p-4">
                    <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
                        <div className="flex flex-col gap-1">
                            <span className="font-semibold text-foreground text-xs">Strategy</span>
                            <SegmentedControl
                                value={strategy}
                                onChange={handleStrategyChange}
                                options={STRATEGY_OPTIONS}
                                disabled={sanitizeMutation.isPending}
                                aria-label="Sanitisation strategy"
                                size="micro"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-3 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
                    <Card
                        title={
                            <Space>
                                <FileText size={16} />
                                <span>Input</span>
                            </Space>
                        }
                        extra={
                            <Space size="small">
                                <Button size="small" onClick={handleLoadSample} disabled={sanitizeMutation.isPending}>
                                    Load sample
                                </Button>
                                <Button
                                    size="small"
                                    icon={<Eraser />}
                                    onClick={handleClear}
                                    disabled={isEmpty && !result}
                                >
                                    Clear
                                </Button>
                            </Space>
                        }
                    >
                        <TextArea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Paste text here…"
                            autoSize={{
                                minRows: 16,
                                maxRows: 28,
                            }}
                            className="font-mono text-xs"
                            disabled={sanitizeMutation.isPending}
                        />
                        <div className="mt-3 flex items-center justify-between gap-3">
                            <span className={`text-xs ${counterClass}`}>
                                {charCount.toLocaleString()} / {MAX_INPUT_CHARS.toLocaleString()} characters
                                {isOverLimit && ' — exceeds limit'}
                            </span>
                            <Button
                                type="primary"
                                icon={<Wand2 />}
                                loading={sanitizeMutation.isPending}
                                disabled={isEmpty || isOverLimit}
                                onClick={handleSanitize}
                            >
                                Sanitise
                            </Button>
                        </div>
                    </Card>

                    <Card
                        title={
                            <Space>
                                <ShieldCheck size={16} />
                                <span>Sanitised output</span>
                            </Space>
                        }
                        extra={
                            <Space size="small">
                                <Button.Icon
                                    icon={showRaw ? <Sparkles /> : <Code2 />}
                                    size="small"
                                    color="gray"
                                    tooltip={showRaw ? 'Show highlighted view' : 'Show raw text'}
                                    onClick={() => setShowRaw((value) => !value)}
                                    disabled={!result}
                                />
                                <Button.Icon
                                    icon={<Copy />}
                                    size="small"
                                    color="gray"
                                    tooltip="Copy redacted text"
                                    onClick={handleCopy}
                                    disabled={!result}
                                />
                            </Space>
                        }
                    >
                        {result ? (
                            <OutputBody
                                chunks={outputChunks}
                                rawText={result.text}
                                showRaw={showRaw}
                                entries={result.entries}
                            />
                        ) : (
                            <div className="flex min-h-[300px] items-center justify-center">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Run sanitise to see the redacted output here"
                                />
                            </div>
                        )}
                    </Card>
                </div>

                {result && result.entries.length > 0 && (
                    <Card
                        className="mt-4"
                        title={
                            <Space>
                                <Layers size={16} />
                                <span>Detected PII</span>
                            </Space>
                        }
                    >
                        <FlatDetectionTable entries={result.entries} />
                    </Card>
                )}
            </div>
        </PageController>
    );
}

type PiiAvailability = 'common' | 'australian';

const PII_TYPE_AVAILABILITY: Record<PiiType, PiiAvailability> = {
    DATE: 'common',
    TIME: 'common',
    EMAIL: 'common',
    PHONE: 'common',
    FULL_NAME: 'common',
    ADDRESS: 'common',
    CARD: 'common',
    IBAN: 'common',
    IP_ADDRESS: 'common',
    MAC_ADDRESS: 'common',
    IMEI: 'common',
    GEO_COORDINATES: 'common',
    SWIFT_BIC: 'common',
    VIN: 'common',
    CRYPTO_WALLET: 'common',
    POLICY_NUMBER: 'common',
    COMPANY: 'common',
    ABN: 'australian',
    ACN: 'australian',
    AFSL: 'australian',
    TFN: 'australian',
    MEDICARE: 'australian',
    DRIVER_LICENCE: 'australian',
    PASSPORT: 'australian',
    BSB: 'australian',
    ACCOUNT: 'australian',
    VEHICLE_REGO: 'australian',
    CRN: 'australian',
    IHI: 'australian',
    HPI: 'australian',
    DVA_FILE: 'australian',
    WORKERS_COMP: 'australian',
    ARBN: 'australian',
    ARSN: 'australian',
    TITLE_REFERENCE: 'australian',
    LOT_PLAN: 'australian',
    STRATA_PLAN: 'australian',
};

const AVAILABILITY_ORDER: ReadonlyArray<{ key: PiiAvailability; label: string }> = [
    { key: 'common', label: 'Common' },
    { key: 'australian', label: 'Australian specific' },
];

const PII_TYPES_BY_AVAILABILITY: Record<PiiAvailability, PiiType[]> = (() => {
    const result: Record<PiiAvailability, PiiType[]> = { common: [], australian: [] };
    for (const type of Object.keys(PII_TYPE_LABEL) as PiiType[]) {
        result[PII_TYPE_AVAILABILITY[type]].push(type);
    }
    return result;
})();

function PageHeader() {
    return (
        <Collapse
            ghost
            className="-ml-3"
            items={[
                {
                    key: 'how',
                    label: <span className="text-xs">How it works</span>,
                    children: (
                        <div className="space-y-3">
                            <p className="mt-0 mr-0 mb-1 ml-0 text-muted-foreground text-sm">
                                Removes personal information from any text. Pick a strategy to control how the tool
                                finds PII; matched values are always replaced with{' '}
                                <span className="font-mono">[TYPE_N]</span> placeholders.
                            </p>
                            <div className="space-y-1">
                                <p className="mb-1 font-semibold text-foreground text-xs">Strategies</p>
                                <ul className="m-0 list-disc space-y-1 pl-5 text-muted-foreground text-xs">
                                    <li>
                                        <span className="font-semibold">In-house</span> — Australian-specific patterns
                                        (dates, emails, phones, ABN, TFN, Medicare, BSB, cards, IBAN) plus an AI pass
                                        for names, addresses, and company references.
                                    </li>
                                    <li>
                                        <span className="font-semibold">Microsoft Presidio</span> — open-source detector
                                        running in a Docker sidecar with the{' '}
                                        <span className="font-mono">spaCy en_core_web_lg</span> NLP model and pattern
                                        recognisers tuned for Australian identifiers. No LLM involved.
                                    </li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <p className="mb-1 font-semibold text-foreground text-xs">Supported PII types</p>
                                <div className="space-y-2">
                                    {AVAILABILITY_ORDER.map(({ key, label }) => {
                                        const types = PII_TYPES_BY_AVAILABILITY[key];
                                        if (types.length === 0) {
                                            return null;
                                        }
                                        return (
                                            <div key={key} className="space-y-1">
                                                <p className="m-0 text-muted-foreground text-xs">{label}</p>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {types.map((type) => {
                                                        const family = PII_TYPE_FAMILY[type];
                                                        return (
                                                            <span
                                                                key={type}
                                                                className={`inline-flex items-center rounded-sm px-1.5 py-px font-mono text-2xs ring-1 ring-inset ${FAMILY_CHIP_CLASS[family]}`}
                                                                title={PII_TYPE_LABEL[type]}
                                                            >
                                                                {type}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    );
}
